// Model možností dopravy — sdílený mezi pokladnou (krok 2, checkoutPage.js)
// a modalem „Možnosti doručení" na detailu produktu (shippingModal.js), aby
// zákazník viděl na obou místech stejné rozdělení i pojmenování metod.
//
// Zdroj dat pro detail je nativní stránka za odkazem `a.shipping-options`
// (`/<slug>:moznosti-dopravy/` — obsah, který Shoptet sám cpe do colorboxu):
// název metody, popis z tooltipu `?`, logo dopravce a cena. Fetch je cachovaný
// per URL, protože tentýž dokument čte i řádek „Doprava od X Kč" v nákupní
// kartě (productDetail.js) — dvakrát se nestahuje.
//
// Fetch jede BEZ COOKIES (`credentials: 'omit'`, zadání Tomáše 2026-09-01):
// Shoptet stránku počítá pro košík + produkt, takže nadrozměr v košíku měnil
// nabídku i „Doprava od X Kč" na detailu ÚPLNĚ JINÉHO produktu. Bez cookies
// server vidí čerstvou session s prázdným košíkem a vrátí nabídku jen pro
// produkt (sonda 2026-09-01: myčka OD011 s křovinořezem v košíku → bez cookies
// plná standardní nabídka 69/119/149/Zdarma). Data jsou tím cart-independent
// a cache se při změně košíku nemusí invalidovat. Vědomý trade-off: fetch
// nenese ani přihlášení — případný individuální ceník dopravy (velkoobchod)
// by tu zákazník neviděl. Pás „kolik chybí do dopravy zdarma" se z téhle
// stránky už NEČTE — jede z dataLayeru `cartInfo` (benefitBand.js), stejná
// čísla jako progress výhod v košíku.
//
// Co v datech NENÍ: termín doručení per dopravce. Shoptet zná jediný termín pro
// celý košík (`div.delivery-time`), API `/shipping-methods` lhůtu nemá vůbec
// (CLAUDE.md „Termín doručení") — proto ho modal ukazuje na úrovni skupiny,
// stejně jako pokladna, a nikdy ho nerozpočítává mezi dopravce.
//
// Osobní odběr má termín VLASTNÍ (`nextPickupDay`): nativní datum je termín
// kurýra, kdežto zboží skladem leží na prodejně a čeká. Počítá se z otevírací
// doby, ne z expedice.

import { isOpenNow, openWindow, openingHours, hoursText } from './headerTools.js';
import { featureOn } from './features.js';
import { inShopWindow } from './shopTime.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/shippingModel.js';

// Výpis otevírací doby bydlí u CONTACT v headerTools (čte ho i patička, 404
// a klientská sekce) — tady se jen reexportuje pro modal a pokladnu.
export { openingHours, hoursText };

// Skupiny dopravy — pořadí dle handoveru pokladny (výdejní místa výchozí
// nahoře). `match` rozhoduje z názvu metody, `icon` a `eta` používá jen modal
// (pokladna kreslí radia a termín řídí vlastním seznamem skupin).
// Osobní odběr je metoda shopu, ne platformy: shop bez prodejny ho v nabídce
// nemá a skupina se nikde nekreslí (manifest `shipping.pickup`).
export const HAS_PICKUP = SHOP.shipping.pickup;

export const SHIP_GROUPS = [
  {
    key: 'box',
    title: L.boxTitle,
    desc: L.boxDesc,
    icon: 'package',
    eta: true,
    match: L.boxRe,
  },
  {
    key: 'address',
    title: L.addressTitle,
    desc: L.addressDesc,
    icon: 'truck',
    eta: true,
  },
  {
    key: 'pickup',
    title: L.pickupTitle,
    // Shop má JEDNU prodejnu (rozhodnutí 2026-07-30) — popis je zároveň adresa.
    desc: L.pickupDesc,
    icon: 'store',
    // Nativní datum je termín doručení kurýrem, ne termín připravení na
    // prodejně — osobní odběr proto nativní termín NEBERE. Svůj termín má
    // (nejbližší otevírací den, `nextPickupDay`), řídí ho klíč skupiny.
    eta: false,
  },
].filter(function (g) {
  return g.key !== 'pickup' || HAS_PICKUP;
});

// Nadrozměrná přeprava: jakmile košík/produkt přesáhne limity běžných metod,
// Shoptet nabídne JEN dopravce označené v adminu jako „dopravce pro atypický
// produkt" (ověřeno 2026-08-21: jediná metoda „PPL – Nadrozměrné zásilky",
// ceník 0–30 kg; „Fofr – Nadměrné balíky" od 30 kg). Zákazníkovi tím beze
// slova zmizí výdejní místa i standardní doprava na adresu — proto vysvětlující
// pás, který sdílí pokladna (nad skupinami kroku 2) i modal na detailu (#225).
export const OVERSIZE_RE = L.oversizeRe;
export const OVERSIZE_NOTE = L.oversizeNote;
// Product-scoped znění pro modal na detailu (data jsou od 2026-09-01 jen pro
// produkt na stránce, ne pro košík — věta o košíku by tam lhala).
export const OVERSIZE_NOTE_PRODUCT = L.oversizeNoteProduct;

// Nabídka spadla na samotnou nadrozměrnou přepravu → místo pásu dopravy zdarma
// se ukazuje OVERSIZE_NOTE. Bere řádky z parseShippingOptions (pokladna má
// vlastní detekci nad widgety, tvar dat se liší).
export function isOnlyOversize(rows) {
  const address = (rows || []).filter(function (r) {
    return r.group === 'address';
  });
  const hasBox = (rows || []).some(function (r) {
    return r.group === 'box';
  });
  return (
    !hasBox &&
    address.length > 0 &&
    address.every(function (r) {
      return OVERSIZE_RE.test(r.name || '');
    })
  );
}

// Nadrozměrné metody („PPL – Nadrozměrné zásilky", „Fofr – Nadměrné balíky")
// nabízí Shoptet KAŽDÉMU košíku ve svém hmotnostním rozsahu — checkbox
// „Dopravce pro atypický produkt" v adminu znamená „smí vézt i atypické", ne
// „jen atypické" (sonda 2026-09-02, ops/sweeps/2026-09-02-doprava-hmotnostni-tarif).
// Pro běžné zboží jsou jen dražší duplikát dopravy na adresu, proto se skrývají,
// kdykoli je v nabídce aspoň jedna běžná doručovaná metoda (výdejní místo nebo
// adresa). Atypickému zboží Shoptet nabídku sám zúží na nadrozměrné metody →
// zůstanou; košík nad limity všech běžných metod taky (jinak by neměl čím
// poslat). Rozhodnutí Tomáše 2026-09-02; totéž pravidlo drží pokladna
// (checkoutPage.js hideRedundantOversize) nad nativními `.radio-wrapper`.
export function hasStandardDelivery(rows) {
  return (rows || []).some(function (r) {
    return (r.group === 'box' || r.group === 'address') && !OVERSIZE_RE.test(r.name || '');
  });
}

export function withoutRedundantOversize(rows) {
  if (!hasStandardDelivery(rows)) return rows;
  return rows.filter(function (r) {
    return !OVERSIZE_RE.test(r.name || '');
  });
}

const PICKUP_RE = L.pickupRe;

// --- osobní odběr: kdy, kde, co je k odběru hned (zadání 2026-08-31) --------
// Sdílí modal „Možnosti doručení" na detailu a karta osobního odběru v kroku 2
// pokladny. Otevírací doba = `CONTACT.openHours` z headerTools (jediný zdroj
// hodin na webu, VČETNĚ soboty); adresa a mapa = `store` z manifestu shopu
// (tatáž adresa jako patička, tentýž odkaz jako řádek Google v hodnocení).
export const PICKUP_ADDRESS = SHOP.store.address;
export const PICKUP_MAP_URL = SHOP.store.mapUrl;
export const PICKUP_MAP_LABEL = L.mapLabel;
// Rozhodnutí Tomáše 2026-08-31: vše, co je na e-shopu skladem, si zákazník
// vyzvedne bez čekání na e-mail — věta se nekvalifikuje podle položek košíku.
// Okamžitý výdej neslibuje (jipos-28, 26. 10.): zboží se nosí ze skladu.
export const PICKUP_STOCK_NOTE = L.stockNote;

const DAY_IN = L.days;

function hourText(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return hh + ':' + (mm < 10 ? '0' : '') + mm;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Nejbližší den, kdy si zákazník může zboží vyzvednout na prodejně.
 *
 * Zboží skladem se vyzvedává bez čekání na e-mail (PICKUP_STOCK_NOTE, rozhodnutí
 * Tomáše 2026-08-31), takže termín vyzvednutí NEJDE přes expedici jako termín
 * doručení — je to prostě nejbližší otevírací den. Dnešek platí, dokud
 * otevírací doba neskončila (i před otevřením: „otevíráme dnes v 7:30").
 *
 * @param {Date} [now]
 * @returns {Date} půlnoc dne vyzvednutí
 */
export function nextPickupDay(now) {
  const d = now || new Date();
  const today = startOfDay(d);
  const h = d.getHours() + d.getMinutes() / 60;
  const win = openWindow(d.getDay());
  // dnešek platí, dokud dnešní okno neskončilo (o víkendu ve 13:00, jindy v 17:00)
  if (win && h < win.to) return today;
  for (let i = 1; i <= 7; i += 1) {
    const day = new Date(today.getTime());
    day.setDate(day.getDate() + i);
    if (openWindow(day.getDay())) return day;
  }
  return today;
}

// Holá fráze dne vyzvednutí — „dnes" / „zítra" / „v neděli" — tentýž den jako
// `nextPickupDay`, jen bez data. Čte ji řádek dopravy v nákupní kartě detailu
// („odběr v Bílině již zítra zdarma") a stav prodejny níž, aby fráze o odběru
// na webu vycházely z jednoho dne (prodejna má otevřeno i o víkendu — dřív
// detail bral den expedice skladu a v pátek večer sliboval odběr až v pondělí).
export function pickupWhen(now) {
  const d = now || new Date();
  const day = nextPickupDay(d);
  const diff = Math.round((day - startOfDay(d)) / 86400000);
  return diff === 0 ? L.today : diff === 1 ? L.tomorrow : DAY_IN[day.getDay()];
}

// „Právě otevřeno — vyzvednout můžete ještě dnes do 17:00" / „Zavřeno —
// otevíráme zítra v 7:30". Den bere `nextPickupDay`, ať se stav prodejny
// a termín vyzvednutí nemůžou rozejít.
export function pickupStatus(now) {
  const d = now || new Date();
  if (isOpenNow(d)) {
    return {
      open: true,
      text: L.openUntil(hourText(openWindow(d.getDay()).to)),
    };
  }
  const day = nextPickupDay(d);
  const win = openWindow(day.getDay());
  if (!win) return { open: false, text: L.closed };
  // hodina otevření je per den (víkend od 8:30) — brát ji z okna toho dne
  return { open: false, text: L.closedOpens(pickupWhen(d), hourText(win.from)) };
}

export const PICKUP_HOURS_LABEL = L.hoursLabel;


// Doprava zdarma platí v tomhle shopu JEN pro výdejní místa (admin → Doprava
// a platby → tabulka „Doprava zdarma od částky": PPL a GLS na výdejní místo).
export const FREE_SHIP_SCOPE = L.freeShipScope;

// Nabízí shop dopravu zdarma vůbec? `shipping.freeShipFrom: null` = ne (na SK
// nemá `allowFreeShipping` ani jedna viditelná metoda, sonda API 2026-09-22).
// Vypíná VŠECHNU komunikaci o dopravě zdarma — i tam, kde by práh přišel
// z dataLayeru: slib, který objednávka nesplní, je horší než mlčení.
export const FREE_SHIPPING = SHOP.shipping.freeShipFrom != null;

// --- hmotnostní pásma dopravy (tarif 2026-09-02) ----------------------------
// Ceník dopravy je od 2026-09-02 hmotnostní: pásma 0–10 / 10–20 / 20–30 kg
// (od 2026-09-05, do té doby 7 / 15 / 30), nad 30 kg jen přeprava pro
// nadměrné balíky; doprava zdarma nad 2 000 Kč platí JEN v prvním pásmu (do
// 10 kg) a jen na výdejní místo (deklarace ceníku
// `tools/admin/doprava-cenik.mjs`, rozhodnutí Tomáše). Shoptet pásmo nikde
// nevydává — zákazník viděl jen jinou cenu bez vysvětlení. Hranice pásem
// jsou proto konstanty šablony (mění se spolu s ceníkem), hmotnost košíku se
// počítá z dataLayeru `cartInfo.cartItems[].weight × quantity` přesně jako
// v ceníku (položka bez váhy = 0, jako v Shoptetu), hmotnost produktu na
// detailu z dataLayeru `product.weight`. Řádek ceníku je „od včetně / do
// exkluzivně": přesně 10,00 kg = pásmo 10–20 (ověřeno sondou 2026-09-02 na
// tehdejší hranici 7 kg).
//
// Práh dopravy zdarma bere každé místo z dataLayeru (`freeShippingFrom`);
// u košíku nad 10 kg ale Shoptet žádný použitelný řádek zdarma nemá a vrací
// `null` — věta „doprava zdarma platí do 10 kg při nákupu nad 2 000 Kč" by tak
// zrovna tam, kde vysvětluje, proč zdarma není, částku neznala. Pro ten
// případ je tu FREE_SHIP_FROM_FALLBACK (zrcadlo řádku ceníku); živá hodnota
// z dataLayeru má vždy přednost. Hodnoty = `shipping` v manifestu shopu.
//
// Shop s PLOCHÝM tarifem (`shipping.weightBands: null`, SK) nemá pásma vůbec:
// `WEIGHT_BANDS` je prázdné, `HAS_WEIGHT_BANDS` false a všechno hmotnostní
// (štítek zásilky v kroku 2, škála pásem, věta o pásmu v modalu, sloupce
// ceníku) se nekreslí — hmotnost tam o ceně nerozhoduje. Zůstává jen hranice,
// nad kterou veze zásilku přeprava pro nadměrné balíky (`OVER_FROM_KG`).
export const HAS_WEIGHT_BANDS = Array.isArray(SHOP.shipping.weightBands);
export const WEIGHT_BANDS = SHOP.shipping.weightBands || [];
export const OVER_FROM_KG = SHOP.shipping.overFromKg;
export const FREE_SHIP_MAX_KG = SHOP.shipping.freeShipMaxKg;
export const FREE_SHIP_FROM_FALLBACK = SHOP.shipping.freeShipFrom;

// Košík BEZ nároku na dopravu zdarma (rozhodnutí Tomáše 2026-09-03: „bez prahu
// z dataLayeru žádný slib"): neprázdný košík, kterému Shoptet nedal práh
// (`freeShippingFrom: null` — nadrozměr, nebo od hmotnostního tarifu košík nad
// 10 kg) a ani neřekl `freeShipping: true`. PROČ práh chybí se tu nerozlišuje —
// v košíku, promo pruhu i trust pásu se doprava zdarma prostě nekomunikuje;
// hmotnost vysvětlí až škála v kroku 2, kde zákazník vidí cenu. Prázdný
// košík nebo nenačtený dataLayer práh jen zatím nezná → false (obecné znění).
export function freeShipExcluded(info) {
  // shop dopravu zdarma nenabízí → nemá co komunikovat u žádného košíku
  if (!FREE_SHIPPING) return true;
  if (!info) return false;
  const items = info.cartItems;
  if (!Array.isArray(items) || !items.length) return false;
  return !(Number(info.freeShippingFrom) > 0) && info.freeShipping !== true;
}

/**
 * Dává Shoptet produktu dopravu zdarma? Odpověď PLATFORMY: nabídka dopravy
 * produktu (parseShippingOptions, fetch bez košíku) nese u výdejního místa cenu 0
 * (`.for-free`), když produkt sám splní řádek ceníku zdarma. Tohle rozhoduje
 * ikonu na detailu (freeShipMark.js) — je to přesně to, co zákazník zaplatí,
 * bez zrcadla prahu. Nadrozměrné metody se nepočítají.
 *
 * @param {Array|null} rows
 * @returns {boolean}
 */
export function offerHasFreeBox(rows) {
  return (rows || []).some(function (r) {
    return r.group === 'box' && !OVERSIZE_RE.test(r.name || '') && (r.free === true || r.price === 0);
  });
}

/**
 * Má SAMOTNÝ produkt (1 ks) nárok na dopravu zdarma — ZRCADLO pravidla ceníku
 * pro místa bez nabídky produktu (karty výpisu, index). Detail rozhoduje
 * odpovědí platformy (`offerHasFreeBox`); sonda `tools/devcheck/free-ship-mark.mjs`
 * hlídá, že se obě cesty shodují. Ikona dopravy zdarma nahrazuje ruční příznak
 * „Doprava zdarma" z adminu, který na cenu dopravy vazbu neměl (rozhodnutí
 * Tomáše 2026-09-23, mock C). Pravidlo = řádek ceníku zdarma: cena s DPH aspoň `freeShipFrom` (práh
 * manifestu — dataLayer ho nese jen za košík), hmotnost ZNÁMÁ a pod
 * `freeShipMaxKg` (řádek „od včetně / do exkluzivně", přesně 10 kg už ne;
 * produkt bez hmotnosti nic neslibuje, i když by ho Shoptet počítal jako 0 kg)
 * a v nabídce produktu je výdejní místo — atypické zboží dostane jen
 * nadrozměrnou přepravu, a ta zdarma není.
 *
 * @param {{price:number, weight:number|null, rows:Array|null}} p
 *   rows = řádky parseShippingOptions nabídky produktu (null = neznámá → ne)
 * @returns {boolean}
 */
export function freeShipQualifies(p) {
  if (!FREE_SHIPPING || !p) return false;
  if (!(Number(p.price) >= FREE_SHIP_FROM_FALLBACK)) return false;
  const kg = Number(p.weight);
  if (!(kg > 0 && kg < FREE_SHIP_MAX_KG)) return false;
  const rows = p.rows || [];
  return (
    !isOnlyOversize(rows) &&
    rows.some(function (r) {
      return r.group === 'box' && !OVERSIZE_RE.test(r.name || '');
    })
  );
}

// Ceny pásem pro škálu hmotnosti v kroku 2 (handover „Objednávka v3" §4.2):
// zákazník vidí vedle své zásilky i sousední pásma, jenže nativní nabídka nese
// jen ceny SVÉHO pásma — ostatní jsou zrcadlo ceníku (`tools/admin/doprava-
// cenik.mjs`, tarif `hmotnostni` od 2026-09-05: výdejní místo 69 / 99 / 149,
// adresa 119 / 139 / 199), index = pásmo z WEIGHT_BANDS. Mění se spolu s ceníkem
// v manifestu shopu (`shipping.bandPrices`).
export const WEIGHT_BAND_PRICES = SHOP.shipping.bandPrices;

// --- akce na dopravu (#322) ---------------------------------------------------
// Overlay nad ceníkem: `bandPrices` se nemění, akce zlevní jen výdejní místo
// JEDNOHO dopravce v prvním pásmu (manifest `shipping.promo`). Platí v okně
// [from, until] času obchodu a jen se zapnutým přepínačem akce (features.js) —
// ostrá ho zapíná až po přepnutí ceníku v adminu, jinak by web sliboval cenu,
// kterou pokladna neúčtuje. Všechny plochy akce (pruh, detail, modal, hero,
// Doprava a platba, škála pásem) se ptají JEN téhle funkce.
const PROMOS = SHOP.shipping.promo || [];

/**
 * Akce na dopravu, která právě platí, jinak null.
 *
 * @param {number} [now] epoch ms (výchozí Date.now())
 * @param {number} [project] projekt Shoptetu pro přepínač (výchozí = stránka)
 * @returns {object|null} položka `shipping.promo` + `was` (cena ceníku bez akce)
 *   a `untilLabel` („25. 10.")
 */
export function shippingPromo(now, project) {
  const explicit = arguments.length > 1;
  const hit = PROMOS.find(function (p) {
    const on = explicit ? featureOn(p.feature, project) : featureOn(p.feature);
    return on && inShopWindow(p.from, p.until, now);
  });
  if (!hit) return null;
  // `partial` = akce jen v části prvního pásma (PPL 49 do 2 kg v pásmu do 10 kg):
  // ceník pásma zůstává, akce je k němu poznámka „49 Kč do 2 kg"
  return Object.assign({}, hit, {
    was: WEIGHT_BAND_PRICES[0].box,
    untilLabel: promoDateLabel(hit.until),
    partial: HAS_WEIGHT_BANDS && hit.maxKg < WEIGHT_BANDS[0],
  });
}

// „2026-10-25T23:59:59" → „25. 10." (mezera za tečkou je NBSP — pruh i štítky
// jsou jeden řádek a zlom uprostřed data by je rozhodil)
export function promoDateLabel(iso) {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? +m[2] + '.\u00A0' + +m[1] + '.' : '';
}

/**
 * Je řádek nabídky dopravy (parseShippingOptions) výdejní místo dopravce akce
 * za AKČNÍ cenu? Rozhoduje odpověď platformy, ne zrcadlo: nabídka produktu nese
 * cenu, kterou zákazník zaplatí. Dopravce se pozná z názvu metody (nabídka
 * kód ani guid nenese) — přejmenování metody v adminu akci na webu jen schová,
 * nikdy nevymyslí. Zboží nad prahem dopravy zdarma má cenu 0 → není to akce.
 */
export function isPromoRow(row, promo) {
  if (!row || !promo) return false;
  return (
    row.group === 'box' &&
    !OVERSIZE_RE.test(row.name || '') &&
    new RegExp('\\b' + promo.carrier + '\\b', 'i').test(row.name || '') &&
    row.price === promo.price
  );
}

// Zbytek tarifu, který škála v pokladně nepotřebuje, ale ceník na stránce
// /doprava-platba/ ano (handover „Doprava a platba v2" §4). Zrcadlo tarifu
// `hmotnostni` v `tools/admin/doprava-cenik.mjs` — mění se spolu s ním (manifest
// shopu, sekce `shipping`).
//
// „PPL – Nadrozměrné zásilky" jede na pásmech adresy s podlahou 149 (nikdy
// levněji než běžná adresa), „Fofr – Nadměrné balíky" je nad posledním pásmem
// jedinou metodou. Limity výdejních míst jsou limity DOPRAVCŮ, ne tarifu:
// zkracují poslední pásmo (GLS tím pásmo 20–30 nemá vůbec, PPL ho veze jen
// do 28 kg).
//
// OVER_PRICE 159 → 299 (zadání Tomáše 2026-09-10): nad 30 kg jede jen Fofr
// a plochých 159 Kč nepokrývalo náklad. Číslo drží ceník i kalkulačka na
// /doprava-platba/ a škála v kroku 2 — mění se VŽDY spolu s tarifem
// `hmotnostni` v `tools/admin/doprava-cenik.mjs` (drift hlídá
// `tools/devcheck/shipping-page.mjs`, které tarif ze skriptu načítá).
export const OVERSIZE_BAND_PRICES = SHOP.shipping.oversizeBandPrices;
export const OVER_PRICE = SHOP.shipping.overPrice;
export const BOX_LIMITS = SHOP.shipping.boxLimits;
// Stropy dopravy na adresu per dopravce (SK: PPL 31,5 / GLS 39,99 kg). Prázdné
// = adresa jede až k `OVER_FROM_KG`, kde ji přebírá přeprava nadměrných balíků.
export const ADDR_LIMITS = SHOP.shipping.addrLimits;
// Prodejna v pořadí „město, ulice" (ceník a řádek odběru na stránce); plná
// adresa s PSČ je PICKUP_ADDRESS.
export const PICKUP_PLACE = SHOP.store.place;

/**
 * Sloupce ceníku dopravy = hranice pásem tarifu. U plochého tarifu je sloupec
 * jediný (do `OVER_FROM_KG`, kde přebírá přeprava nadměrných balíků) — tabulka
 * tím drží týž tvar pro oba tarify a stránka nemusí větvit rozvržení.
 *
 * @returns {number[]} horní hranice sloupců v kg (bez koncového „nad")
 */
export function tableBands() {
  return HAS_WEIGHT_BANDS ? WEIGHT_BANDS : [OVER_FROM_KG];
}

/**
 * Ceník metoda × pásmo pro stránku Doprava a platba — složený z týchž
 * konstant, ze kterých počítá škála v pokladně, aby se tabulka a tarif
 * nemohly rozejít. Sloupců je o jeden víc než pásem (poslední = nad limitem).
 *
 * Buňka: `price` = cena v měně shopu (0 = zdarma, null = metoda pásmo neveze),
 * `free` = v pásmu platí doprava zdarma nad prahem, `limit` = strop dopravce,
 * který pásmo zkracuje, `over` = strop, nad kterým už metoda nejezdí.
 *
 * Řádky, které shop nemá, ve výčtu nejsou: nadrozměrná metoda bez ceny
 * (`oversizeBandPrices: []`) ani osobní odběr bez prodejny (`pickup: false`).
 *
 * @returns {{key:string, name:string, desc:string, cells:object[]}[]}
 */
export function shippingPriceTable() {
  const bands = tableBands();
  const cell = (price, extra) => Object.assign({ price: price == null ? null : price }, extra);
  const tail = (c) => bands.map(c).concat([cell(null)]);

  // dopravce má vlastní strop: nad ním pásmo nejede vůbec, pásmo useknuté
  // stropem nese poznámku „jen do N kg"
  const limited = (limit, price, i, hi) => {
    const lo = i === 0 ? 0 : bands[i - 1];
    if (limit && limit <= lo) return cell(null, { over: limit });
    const c = cell(price, i === 0 && FREE_SHIPPING ? { free: true } : null);
    if (limit && limit < hi) c.limit = limit;
    return c;
  };

  // akce na dopravu (#322): výdejní místo dopravce akce v prvním pásmu nese
  // akční cenu a `promo` (přeškrtnutá cena ceníku + konec akce)
  const pr = shippingPromo();
  const boxRow = (carrier, desc) => ({
    key: 'box-' + carrier.toLowerCase(),
    name: L.boxRowName(carrier),
    desc: desc,
    cells: bands
      .map(function (hi, i) {
        const c = limited(BOX_LIMITS[carrier], WEIGHT_BAND_PRICES[i].box, i, hi);
        if (pr && pr.carrier === carrier && i === 0 && c.price !== null) {
          // akce v části pásma: buňka drží cenu pásma, akce je poznámka pod ní
          if (pr.partial) {
            c.promo = { price: pr.price, maxKg: pr.maxKg, until: pr.untilLabel, partial: true };
          } else {
            c.price = pr.price;
            c.promo = { was: pr.was, until: pr.untilLabel };
          }
        }
        return c;
      })
      .concat([cell(null)]),
  });

  const addrRow = (carrier) => ({
    key: 'addr-' + carrier.toLowerCase(),
    name: L.addrRowName(carrier),
    desc: L.addrRowDesc,
    cells: bands
      .map(function (hi, i) {
        const c = limited(ADDR_LIMITS[carrier], WEIGHT_BAND_PRICES[i].addr, i, hi);
        // doprava zdarma je na výdejních místech, ne na adrese
        delete c.free;
        return c;
      })
      .concat([cell(null)]),
  });

  const rows = [
    boxRow('GLS', L.glsBoxDesc(BOX_LIMITS.GLS)),
    boxRow('PPL', L.pplBoxDesc(BOX_LIMITS.PPL)),
    addrRow('PPL'),
    addrRow('GLS'),
  ];
  if (OVERSIZE_BAND_PRICES.length) {
    rows.push({
      key: 'oversize',
      name: L.oversizeName,
      desc: L.oversizeDesc,
      cells: tail(function (hi, i) {
        return cell(OVERSIZE_BAND_PRICES[i]);
      }),
    });
  }
  rows.push({
    key: 'fofr',
    name: L.fofrName,
    desc: L.fofrDesc,
    cells: bands
      .map(function () {
        return cell(null);
      })
      .concat([cell(OVER_PRICE)]),
  });
  if (HAS_PICKUP) {
    rows.push({
      key: 'pickup',
      name: L.pickupName,
      desc: PICKUP_PLACE + ' · ' + hoursText(),
      cells: bands
        .map(function () {
          return cell(0);
        })
        .concat([cell(0)]),
    });
  }
  return rows;
}

const NBSP = '\u00A0';
const NDASH = '–';

export function formatKg(kg) {
  const n = Number(kg);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 100) / 100;
  return rounded.toLocaleString(SHOP.shop.language, { maximumFractionDigits: 2 }).replace(/\s/g, NBSP) + NBSP + L.kg;
}

/**
 * Hmotnost košíku z dataLayeru `cartInfo` (Σ weight × quantity).
 *
 * @param {object|null} info dataLayer `cartInfo`
 * @returns {number|null} kg; null bez položek nebo když žádná položka váhu nenese
 */
export function cartWeight(info) {
  const items = info && Array.isArray(info.cartItems) ? info.cartItems : null;
  if (!items || !items.length) return null;
  let known = false;
  let total = 0;
  items.forEach(function (it) {
    // hmotnost chodí jako string („8.500") — proto Number()
    const w = it && it.weight != null && it.weight !== '' ? Number(it.weight) : NaN;
    if (!Number.isFinite(w)) return;
    known = true;
    const q = Number(it.quantity);
    total += w * (Number.isFinite(q) && q > 0 ? q : 1);
  });
  if (!known || total <= 0) return null;
  return Math.round(total * 1000) / 1000;
}

/**
 * Hmotnost produktu na detailu z dataLayeru `product` (`weight`; u variant
 * `codes[].weight` podle aktuálního kódu, když je známý).
 *
 * @param {object|null} product dataLayer `product`
 * @param {string} [code] kód zvolené varianty
 * @returns {number|null}
 */
export function productWeight(product, code) {
  if (!product) return null;
  let w = NaN;
  if (code && Array.isArray(product.codes)) {
    const hit = product.codes.find(function (c) {
      return c && c.code === code;
    });
    if (hit && hit.weight != null) w = Number(hit.weight);
  }
  if (!Number.isFinite(w) && product.weight != null) w = Number(product.weight);
  return Number.isFinite(w) && w > 0 ? w : null;
}

/**
 * Pásmo ceníku pro danou hmotnost.
 *
 * @param {number} kg
 * @returns {{key:string, label:string, free:boolean, over:boolean, index:number}|null}
 *   label = „do 10 kg" / „10–20 kg" / „20–30 kg" / „nad 30 kg";
 *   free = pásmo, ve kterém může platit doprava zdarma (do 10 kg);
 *   over = nad limitem běžných metod (jen přeprava pro nadměrné balíky);
 *   index = pořadí ve WEIGHT_BANDS / WEIGHT_BAND_PRICES (−1 nad limitem)
 */
export function bandOf(kg) {
  // null/'' = váha neznámá (Number(null) je 0 a spadlo by to do „do 10 kg")
  if (kg == null || kg === '') return null;
  const n = Number(kg);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n >= OVER_FROM_KG) {
    return { key: 'over', label: L.bandOver + NBSP + OVER_FROM_KG + NBSP + L.kg, free: false, over: true, index: -1 };
  }
  // plochý tarif: pod hranicí nadměrných balíků žádné pásmo není (cena je
  // jedna) → nic hmotnostního se nekreslí
  if (!HAS_WEIGHT_BANDS) return null;
  let lo = 0;
  for (let i = 0; i < WEIGHT_BANDS.length; i += 1) {
    const hi = WEIGHT_BANDS[i];
    if (n < hi) {
      return i === 0
        ? { key: 'b0', label: L.bandUpTo + NBSP + hi + NBSP + L.kg, free: hi <= FREE_SHIP_MAX_KG, over: false, index: 0 }
        : { key: 'b' + i, label: lo + NDASH + hi + NBSP + L.kg, free: false, over: false, index: i };
    }
    lo = hi;
  }
  return null;
}

/**
 * Popisky všech pásem ceníku pro škálu hmotnosti (stejné znění jako `bandOf`).
 *
 * @returns {{label:string, index:number, box:number, addr:number}[]}
 */
export function weightBandRows() {
  // plochý tarif pásma nemá → škála se nikde nekreslí
  if (!HAS_WEIGHT_BANDS) return [];
  // akce na dopravu (#322): první pásmo nese `promo` — výdejní místo je pak
  // „od" akční ceny (akce platí jen pro jednoho dopravce, druhý má ceník)
  const pr = shippingPromo();
  return WEIGHT_BANDS.map(function (hi, i) {
    const p = WEIGHT_BAND_PRICES[i] || {};
    const label = i === 0 ? L.bandUpTo + NBSP + hi + NBSP + L.kg : WEIGHT_BANDS[i - 1] + NDASH + hi + NBSP + L.kg;
    const row = { label: label, index: i, box: p.box, addr: p.addr };
    if (pr && i === 0) row.promo = { carrier: pr.carrier, price: pr.price, maxKg: pr.maxKg, until: pr.untilLabel };
    return row;
  });
}

// Texty pásma — jedno znění pro pokladnu i modal na detailu (mock 2026-09-02,
// handover „Objednávka v3" 2026-09-02). Pokladna od v3 nese jen štítek
// hmotnosti v hlavičce karty a škálu pásem pod skupinami; věty o pásmu
// (patička, suffix za názvem metody, řádek v košíku) padly.
export const WEIGHT_CHIP_LEAD = L.chipLead;
export const WEIGHT_SCALE_TITLE = L.scaleTitle;
export const WEIGHT_SCALE_BOX = L.scaleBox;
export const WEIGHT_SCALE_ADDR = L.scaleAddr;
// věty o dopravě zdarma vznikají jen v shopu, který ji nabízí (a s pásmy)
export const WEIGHT_SCALE_NOTE_A = FREE_SHIPPING ? L.scaleNoteA(FREE_SHIP_MAX_KG, FREE_SHIP_SCOPE) : null;
export const WEIGHT_SCALE_OVER_LABEL = L.scaleOverLabel;
export const WEIGHT_FOOT_OVER = L.footOver(OVER_FROM_KG);
export const WEIGHT_PRODUCT_A = L.productA;
export const WEIGHT_PRODUCT_B = L.productB;
export const WEIGHT_PRODUCT_SUM = L.productSum;
export const WEIGHT_PRODUCT_FREE_A = FREE_SHIPPING ? L.productFreeA(FREE_SHIP_MAX_KG) : null;
export const WEIGHT_PRODUCT_FREE_B = L.productFreeB;

// Ikony plateb podle názvu metody (mock 2026-09-02): dobírka = bankovka,
// převod/QR = banka, prodejna = peněženka, karta online (Shoptet Pay, Apple/
// Google Pay — jen na ostré) = karta; neznámé = karta. Prodejna PŘED kartou:
// „Hotově / kartou na prodejně" nese slovo karta taky.
export const PAY_ICON_RULES = [
  { re: L.payCashRe, icon: 'cash' },
  { re: L.payBankRe, icon: 'bank' },
  { re: L.payWalletRe, icon: 'wallet' },
  { re: L.payCardRe, icon: 'card' },
];

export function paymentIcon(name) {
  const hit = PAY_ICON_RULES.find(function (r) {
    return r.re.test(name || '');
  });
  return hit ? hit.icon : 'card';
}

// Zařazení metody podle názvu. Pokladna má před tímhle ještě tvrdý signál
// `.personal-collection-point-id`, popup nativní stránky ho nenese.
export function shippingGroupKey(name) {
  const box = SHIP_GROUPS.find(function (g) {
    return g.match && g.match.test(name || '');
  });
  if (box) return box.key;
  if (PICKUP_RE.test(name || '')) return 'pickup';
  return 'address';
}

function textOf(el) {
  return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

function priceValue(text) {
  const m = String(text || '')
    .replace(/[\s ]/g, '')
    .match(/(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) : null;
}

// Tooltip nese popis metody v `title`; default JS Shoptetu ho na živé stránce
// stěhuje do `data-original-title` (v našem odpojeném dokumentu se nespustí,
// ale čteme obojí — stejná past jako u info pásu plateb v pokladně).
function tooltipText(row) {
  const tip = row.querySelector('.question-tooltip, .show-tooltip');
  if (!tip) return '';
  return (tip.getAttribute('title') || tip.getAttribute('data-original-title') || '').trim();
}

export function parseShippingOptions(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const rows = [...doc.querySelectorAll('.shipping-row')].map(function (row) {
    const name = textOf(row.querySelector('.shipping-billing-name'));
    const priceEl = row.querySelector('.payment-shipping-price');
    const img = row.querySelector('.payment-logo img');
    return {
      name: name,
      desc: tooltipText(row),
      logo: img && img.getAttribute('src') ? img.getAttribute('src') : null,
      priceText: textOf(priceEl),
      price: priceEl && priceEl.classList.contains('for-free') ? 0 : priceValue(textOf(priceEl)),
      free: !!(priceEl && priceEl.classList.contains('for-free')),
      oversize: OVERSIZE_RE.test(name),
      group: shippingGroupKey(name),
    };
  });
  if (!rows.length) return null;
  return {
    rows: rows,
    note: textOf(doc.querySelector('.shipping-options-popup > p')),
  };
}

// --- sdílený fetch ----------------------------------------------------------
// Vrací `null` (ne odmítnutý slib), když data nejsou — volající se pak drží
// nativního chování místo prázdného modalu.
//
// `credentials: 'omit'` = bez cookies → server počítá nabídku pro produkt sám,
// bez vlivu košíku (viz hlavička souboru). Data jsou tím cart-independent,
// cache per URL žije po celý pageview a nic ji nemusí invalidovat.
const cache = Object.create(null);

// `opts.withCart` = nabídka pro KOŠÍK (cookies jdou s sebou → Shoptet počítá
// košík + produkt, tj. přesně to, co košík reálně dostane). Používá jen řádek
// termínu v košíku (checkoutPage.js initCartEta), kde se rozhoduje, jestli
// smí slíbit sobotu s GLS — bez cookies by GLS „bylo v nabídce" i u košíku
// s nadrozměrem. Vlastní klíč cache, ať se obě nabídky nepletou.
// `opts.cartKey` = otisk košíku (kód:množství). Košík se v témže pageview mění
// AJAXem a nabídka pro starý košík by slíbila dopravce, kterého nový košík
// (přes váhový limit, nadrozměr) už nemá — otisk je proto součást klíče
// (nález B1-04 z code review 2026-09-09).
export function loadShippingOptions(href, opts) {
  if (!href || !window.fetch || location.protocol === 'file:') return Promise.resolve(null);
  const withCart = !!(opts && opts.withCart);
  const key = withCart ? href + '#cart:' + ((opts && opts.cartKey) || '') : href;
  if (cache[key]) return cache[key];
  cache[key] = fetch(href, { credentials: withCart ? 'include' : 'omit' })
    .then(function (r) {
      return r.ok ? r.text() : '';
    })
    .then(function (html) {
      const data = html ? parseShippingOptions(html) : null;
      // nadrozměrné metody jen tam, kde je nabídka opravdu nadrozměrná
      // (viz withoutRedundantOversize) — čte to detail i modal, filtr je tu
      // jednou pro oba
      if (data && data.rows) data.rows = withoutRedundantOversize(data.rows);
      return data;
    })
    .catch(function () {
      return null;
    });
  return cache[key];
}
